import React from 'react';
import { useI18n } from '../../i18n';
import { useEmail } from '../../context/EmailContext';
import { EmailPreviewSkeleton } from './EmailSkeleton';
import { FaFileAlt } from 'react-icons/fa';
import replyIcon from '../../assets/icons/ArrowBendUpLeft.svg';
import replyAllIcon from '../../assets/icons/ArrowBendDoubleUpLeft.svg';
import forwardIcon from '../../assets/icons/FastForward.svg';
import starIcon from '../../assets/icons/Star.svg';
import trashIcon from '../../assets/icons/TrashEmail.svg';

/**
 * C1: Client-side HTML sanitization for email body.
 * Strips dangerous attributes (on*, href=javascript:, src=data:) while preserving
 * all visual formatting. The backend already sanitizes on write, but this adds a
 * defence-in-depth layer for emails that may have been synced before sanitization.
 */
const sanitizeEmailHtml = (html) => {
  if (!html) return '';
  // Remove <script> and <style> blocks entirely
  let safe = html.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    ''
  );
  safe = safe.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
  // Strip on* event attributes (onclick, onload, onerror, etc.)
  safe = safe.replace(/\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi, '');
  // Strip javascript: URIs in href/src/action
  safe = safe.replace(
    /(href|src|action)\s*=\s*["']?\s*javascript:[^"'>]*/gi,
    '$1="#"'
  );
  // Strip data: URIs in src (can embed malicious content)
  safe = safe.replace(/src\s*=\s*["']?\s*data:[^"'>]*/gi, 'src=""');
  return safe;
};

const ArchiveIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="21 8 21 21 3 21 3 8" />
    <rect x="1" y="3" width="22" height="5" />
    <line x1="10" y1="12" x2="14" y2="12" />
  </svg>
);

const EmailPreview = ({ onReply, onForward, onEditDraft, mobileView, navigateToView }) => {
  const { t } = useI18n();
  const {
    activeEmail,
    activeEmailId,
    activeFolderId,
    toggleStar,
    toggleArchive,
    deleteEmail,
    markAsRead,
  } = useEmail();

  // No email selected yet
  if (!activeEmailId) {
    return (
      <div className="email-preview-panel">
        <div className="email-preview-empty">{t('pages.email.preview.empty')}</div>
      </div>
    );
  }

  // Email selected but not yet loaded — show skeleton
  if (!activeEmail) {
    return (
      <div className="email-preview-panel">
        <EmailPreviewSkeleton />
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name, email) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return email ? email[0].toUpperCase() : '?';
  };

  const formatRecipients = (recipients) => {
    if (!recipients || recipients.length === 0) return '';
    return recipients.map((r) => r.name || r.email).join(', ');
  };

  return (
    <div className="email-preview-panel">
      <div className="email-preview-header">
        <button 
            className="email-mobile-back-btn" 
            onClick={() => navigateToView && navigateToView('list')}
            title="Back to list"
        >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span>{t('pages.email.preview.back_to_list') || 'Back'}</span>
        </button>
        <h2 className="email-preview-subject">
          {activeEmail.subject || t('pages.email.preview.no_subject')}
        </h2>

        <div className="email-preview-meta">
          <div className="email-preview-avatar">
            {getInitials(activeEmail.from?.name, activeEmail.from?.email)}
          </div>

          <div className="email-preview-info">
            <div className="email-sender-name-row">
              <span className="email-sender-name">
                {activeEmail.from?.name || activeEmail.from?.email}
              </span>
              {activeEmail.from?.name && (
                <span className="email-sender-email">
                  &lt;{activeEmail.from?.email}&gt;
                </span>
              )}
            </div>
            <div className="email-preview-to">
              {t('pages.email.preview.to')}: {formatRecipients(activeEmail.to)}
              {activeEmail.cc?.length > 0 && (
                <span> | {t('pages.email.preview.cc')}: {formatRecipients(activeEmail.cc)}</span>
              )}
            </div>
          </div>

          <div className="email-preview-actions">
            {activeEmail.is_draft ? (
              <button
                className="email-action-btn ui-font-12"
                onClick={() => onEditDraft && onEditDraft(activeEmail)}
                title={t('pages.email.preview.edit_draft')}
                style={{ padding: '4px 8px' }}
              >
                {t('pages.email.preview.edit_draft')}
              </button>
            ) : (
              <>
                <button
                  className="email-action-btn"
                  onClick={() => onReply(activeEmail, false)}
                  title={t('pages.email.preview.reply')}
                >
                  <img
                    src={replyIcon}
                    alt={t('pages.email.preview.reply')}
                    className="email-action-icon"
                  />
                </button>
                <button
                  className="email-action-btn"
                  onClick={() => onReply(activeEmail, true)}
                  title={t('pages.email.preview.reply_all')}
                >
                  <img
                    src={replyAllIcon}
                    alt={t('pages.email.preview.reply_all')}
                    className="email-action-icon"
                  />
                </button>
                <button
                  className="email-action-btn"
                  onClick={() => onForward(activeEmail)}
                  title={t('pages.email.preview.forward')}
                >
                  <img
                    src={forwardIcon}
                    alt={t('pages.email.preview.forward')}
                    className="email-action-icon"
                  />
                </button>
              </>
            )}
            <button
              className="email-action-btn ui-font-12"
              onClick={() => markAsRead(activeEmail.id, false)}
              title={t('pages.email.preview.mark_unread')}
              style={{ padding: '4px 8px' }}
            >
              {t('pages.email.preview.unread')}
            </button>
            <button
              className={`email-action-btn ${activeEmail.is_starred ? 'starred' : ''}`}
              onClick={() => toggleStar(activeEmail.id)}
              title={activeEmail.is_starred ? 'Unstar' : 'Star'}
            >
              <img src={starIcon} alt="Star" className="email-action-icon" />
            </button>
            <button
              className={`email-action-btn${activeEmail.folder?.name === 'Archive' ? ' archived' : ''}`}
              onClick={() =>
                toggleArchive(
                  activeEmail.id,
                  activeEmail.folder?.name === 'Archive'
                )
              }
              title={
                activeEmail.folder?.name === 'Archive' ? t('pages.email.preview.unarchive') : t('pages.email.preview.archive')
              }
            >
              <ArchiveIcon />
            </button>
            <button
              className="email-action-btn"
              onClick={() => deleteEmail(activeEmail.id)}
              title={t('pages.email.preview.delete')}
            >
              <img src={trashIcon} alt={t('pages.email.preview.delete')} className="email-action-icon" />
            </button>
          </div>
        </div>
      </div>

      <div className="email-preview-body">
        <div
          className="email-body-content"
          dangerouslySetInnerHTML={{
            __html: sanitizeEmailHtml(
              activeEmail.body_html ||
                activeEmail.body_text?.replace(/\n/g, '<br>') ||
                ''
            ),
          }}
        />
      </div>

      {activeEmail.attachments?.filter((a) => a.disposition !== 'inline')
        .length > 0 && (
        <div className="email-attachments">
          <div className="email-attachments-title">
            {t('pages.email.preview.attachments')} ({activeEmail.attachments.filter((a) => a.disposition !== 'inline').length})
          </div>
          <div className="email-attachment-list">
            {activeEmail.attachments
              .filter((a) => a.disposition !== 'inline')
              .map((attachment) => (
                <a
                  key={attachment.id}
                  href={attachment.download_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="email-attachment-item"
                >
                  <FaFileAlt className="attachment-icon" />
                  <span className="attachment-name">{attachment.filename}</span>
                  <span className="attachment-size">
                    {attachment.human_size}
                  </span>
                </a>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPreview;
